import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors } from '@pattern-library/form-input';
const propTypes = {
  /**
   * Child checkboxes
   **/
  children: PropTypes.node.isRequired,
  /**
   * Additional custom classes for the fieldset element
   **/
  className: PropTypes.string,
  /**
   * The severity of the input's error state
   **/
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * Enable border checkbox style
   **/
  hasBorder: PropTypes.bool,
  /**
   * Error state flag
   **/
  hasError: PropTypes.bool,
  /**
   * Hides the legend from sighted users, but is read out by screen readers
   **/
  hideLegend: PropTypes.bool,
  /**
   * The Checkbox Container's ID
   */
  id: PropTypes.string,
  /**
   * Disabled state flag
   **/
  isDisabled: PropTypes.bool,
  /**
   * Checkboxes are stretched evenly to fill space
   **/
  isFullWidth: PropTypes.bool,
  /**
   * Whether 1 or more selected options is required
   **/
  isRequired: PropTypes.bool,
  /**
   * Content of the info popover. Enables the popover component when provided.
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Fieldset legend. Can be a string or element
   **/
  legend: PropTypes.node,
  /**
   * Custom styles for the legend
   **/
  legendStyles: PropTypes.string,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * The display orientation of Checkboxes
   */
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * QA data attribute for automated testing
   **/
  qaId: PropTypes.string,
  /**
   * Whether to show the asterisk or not in the UI
   **/
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Text message for below the CheckboxContainer
   */
  subtext: PropTypes.string
};
const defaultProps = {
  className: '',
  errorLevel: 'error',
  errorMessage: null,
  hasBorder: false,
  hasError: false,
  hideLegend: false,
  infoPopoverContent: null,
  infoPopoverProps: {},
  isDisabled: false,
  isFullWidth: false,
  isRequired: false,
  optionalInputLabel: '(optional)',
  orientation: 'horizontal',
  qaId: '',
  showRequiredAsterisk: false
};

// All props are defined here, whether they are used or not,
// in order to prevent them from being passed to the input through checkboxContainerPassthrough
const CheckboxContainer = props => {
  const {
    children,
    className,
    errorLevel,
    errorMessage,
    hasBorder,
    hasError,
    hideLegend,
    id,
    infoPopoverContent,
    infoPopoverProps,
    isDisabled,
    isFullWidth,
    isRequired,
    legend,
    optionalInputLabel,
    orientation,
    qaId,
    showRequiredAsterisk,
    subtext,
    ...checkboxContainerPassthrough
  } = props;
  const fieldSetClasses = classNames('asc-form-group', 'asc-form-group--checkbox', className, {
    'asc-form-group--checkbox-vertical': orientation === 'vertical',
    'asc-form-input--error': hasError && errorMessage && errorLevel === 'error',
    'asc-form-input--warning': hasError && errorMessage && errorLevel === 'warning',
    'is-disabled': isDisabled
  });
  const legendClasses = classNames('asc-form-label', {
    'asc-acc-hidden': hideLegend
  });
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  return /*#__PURE__*/React.createElement("fieldset", _extends({
    className: fieldSetClasses,
    "data-qa": qaId
  }, checkboxContainerPassthrough), /*#__PURE__*/React.createElement("legend", {
    className: legendClasses
  }, legend, isRequired && showRequiredAsterisk && /*#__PURE__*/React.createElement("span", {
    className: "asc-form-required-asterisk"
  }, "\xA0*"), !isRequired && optionalInputLabel && /*#__PURE__*/React.createElement("span", null, "\xA0", optionalInputLabel), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  })), /*#__PURE__*/React.createElement("div", {
    className: "asc-checkbox-group-error-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('asc-checkbox-group-layout', {
      'asc-checkbox-group-layout--full-width': isFullWidth || hasBorder && !isFullWidth
    })
  }, children)), /*#__PURE__*/React.createElement("div", {
    className: "asc-checkbox-group-message-container"
  }, /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext asc-m-0 asc-p-0"
  }, subtext)));
};
CheckboxContainer.propTypes = propTypes;
CheckboxContainer.defaultProps = defaultProps;
export default CheckboxContainer;